import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Router } from "@angular/router";
import { catchError, map } from "rxjs/operators";
import * as Sentry from "@sentry/angular-ivy";
import { Observable } from "rxjs";

export type CreatePrepQuestionsRequest = {
  actionItems: string[]
}

export type PrepQuestionsResponse = {
  questions: {
    id: string
    source: "ai" | "catalog" | "manual"
    questionText: string
  }[]
}

export type AuthenticatedCleanup = () => void

export type BookNextSessionRequest = {
  date: string
  actionItems: string[]
  prepQuestions: {
    questionSource: "ai" | "catalog" | "manual"
    questionText: string
  }[]
}

export type PrepQuestionSource = "ai" | "catalog" | "manual"

export type SubmitPrepQuestionsRequest = {
  prepQuestions: {
    questionSource: PrepQuestionSource
    questionText: string
  }[]
}

export type PrepQuestionsReviewDashboardTask = {
  id: string
  type: "prep-questions.review"
  journey: { id: number }
  session: { id: number; number: number }
  coachee: { firstName: string; lastName: string }
}

export type PrepQuestionsReviewSessionTask = {
  id: string
  type: "prep-questions.review"
}

export type CoachDashboardTask = PrepQuestionsReviewDashboardTask
export type CoachSessionTask = PrepQuestionsReviewSessionTask

export type CoachDashboardResponse = {
  tasks: CoachDashboardTask[]
}

export type MeResponse =
  | { authenticated: false }
  | { authenticated: true, role: "coach" }

export type ListJourneysCommand = {
  offset: number
  limit: number
  search?: string
  completed?: boolean
}

export type ListJourneysResponse = {
  journeys: JourneyResponse[];
  totalJourneys: number;
}

export type JourneyResponse = {
  id: number;
  isCompleted: boolean;
  objectives: string;
  useCase: {
    title: string;
  };
  topic: {
    name: string;
  };
  coachee: {
    firstName: string;
    lastName: string;
    email: string;
    fieldOfWork?: number;
    workExperience?: string;
  };
  company: {
    name: string;
  };
  maxSessions: number;
  remainingSessions: number;
  startedAt: string;
  endedAt?: string;
  preferredLanguage: string;
  upcomingSession?: EmbeddedSessionResponse;
  pastSessions: EmbeddedSessionResponse[];
  totalFileSizeKb: number;
  actions: string[];
  indicators: {
    newPrepQuestionAnswers: number;
    newFiles: number;
  };
}

export type JourneyDetailsResponse = JourneyResponse&{
  files: EmbeddedFileDto[];
  noteFromSparrks?: string;
  programNote?: string;
  hasProgramBriefing: boolean;
  hasCoacheeBriefing: boolean;
}

export type EmbeddedFileDto = {
  id: number;
  name: string;
  sizeKb: number;
  isNew: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
};

export type SessionIndicators = {
  newPrepQuestionAnswers?: number;
};

export type EmbeddedSessionResponse = {
  id: number;
  number: number;
  prepQuestions: {
    question: string;
    answer?: string;
  }[];
  joinLink?: string;
  isBeingRescheduled: boolean;
  date: string;
  notes?: string;
  indicators?: SessionIndicators;
  tasks?: CoachSessionTask[];
}

@Injectable({ providedIn: 'root' })

export class CoachHttpService {
  DeleteFile(file_id: any) {
    throw new Error("Method not implemented.");
  }

  constructor(private _http: HttpClient, private router: Router) { }

  checkUnauthorized(err) {
    if (err?.status === 401) {
      this.router.navigate(['account/coach-login']);
      throw "unauthorized"
    }
  }

  get<R>(path: string) {
    let headers = new HttpHeaders()
    headers.set("Accept", "application/json")
    return this._http.get(path, {headers}).pipe(
      map(r => r as R),
      catchError((err) => {
        this.checkUnauthorized(err)
          if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
          }
        trackFetchError('GET', path, err)
          throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

  post<R>(path: string, body: any|null) {
    let headers = new HttpHeaders()
    headers.set("Accept", "application/json")
    return this._http.post(path, body, {headers}).pipe(
      map(r => r as R),
      catchError((err) => {
        this.checkUnauthorized(err)
        if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
        }
        trackFetchError('POST', path, err, body)
        throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

  put<R>(path: string, body: any) {
    let headers = new HttpHeaders()
    headers.set("Accept", "application/json")
    return this._http.put(path, body, {headers}).pipe(
      map(r => r as R),
      catchError((err) => {
        this.checkUnauthorized(err)
        if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
        }
        trackFetchError('PUT', path, err, body)
        throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

  getMe() {
    return this.get<MeResponse>('/api/v2/coach/me')
  }

  getMeAndRedirectOnUnauthorized(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.getMe()
      .toPromise()
      .then((r) => {
        if (r.authenticated) {
          resolve()
        } else {
          this.router.navigate(['account/coach-login']);
        }
      });
    })
  }

  setupAuthGuard(): Promise<AuthenticatedCleanup> {
    const me = this;
    return new Promise<AuthenticatedCleanup>((resolve) => {
      this.getMeAndRedirectOnUnauthorized().then(() => {
        window.onfocus = function() {
          me.getMeAndRedirectOnUnauthorized()
        }
        resolve(() => {
          window.onfocus = null;
        })
      })
    })
  }

  del<R>(path: string) {
    let headers = new HttpHeaders()
    headers.set("Accept", "application/json")
    return this._http.delete(path, {headers}).pipe(
      map(r => r as R),
      catchError((err) => {
        this.checkUnauthorized(err)
        if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
        }
        trackFetchError('DELETE', path, err)
        throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

   //login api
  postCoachLogin(login_info) {
    return this.post<any>('/api/v1/coach/authenticate_login', login_info)
  }


  // sendPasswordToEmail
  sendPasswordToEmail(email_info) {
    return this.post<any>('/api/v1/coach/forgot_password', email_info);
  }

  verifyPasswordResetToken(token_info) {
    return this.post<any>('/api/v1/coach/decrypt_reset_password_data', token_info);
  }

  verifyPasswordSetToken(token_info) {
    return this.post<any>('/api/v1/coach/decrypt_setup_password_data', token_info);
  }

  verifyTimeSlotConfirmToken(token_info) {
    return this.post<any>('/api/v1/coach/decrypt_time_slot_data', token_info);
  }

  verifyReschedulingTimeSlotConfirmToken(token_info) {
    return this.post<any>('/api/v1/coach/decrypt_rescheduling_request_data', token_info);
  }

  resetPassword(password_details) {
    let headers = new HttpHeaders().set('token', localStorage.getItem('resetpasswordToken'));
    headers.set("Accept", "application/json")
    const path = '/api/v1/coach/reset_password'
    return this._http.post(path, password_details, {headers}).pipe(
      map(r => r as any),
      catchError((err) => {
        this.checkUnauthorized(err)
        if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
        }
        trackFetchError('POST', path, err)
        throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

  setPassword(password_details) {
    let headers = new HttpHeaders().set('token', localStorage.getItem('setpasswordToken'));
    headers.set("Accept", "application/json")
    const path = '/api/v1/coach/setup_password'
    return this._http.post(path, password_details, {headers}).pipe(
      map(r => r as any),
      catchError((err) => {
        this.checkUnauthorized(err)
        if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
        }
        trackFetchError('POST', path, err)
        throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

  confirmedTimeSlot(timeslot_details) {
    let headers = new HttpHeaders().set('token', localStorage.getItem('timeslot_confirm_page_token'));
    headers.set("Accept", "application/json")
    const path = '/api/v1/coach_assign_timeslot'
    return this._http.post(path, timeslot_details, {headers}).pipe(
      map(r => r as any),
      catchError((err) => {
        this.checkUnauthorized(err)
        if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
        }
        trackFetchError('POST', path, err, timeslot_details)
        throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

  confirmedReschedulingTimeSlot(timeslot_details) {
    let headers = new HttpHeaders().set('token', localStorage.getItem('rescheduled_timeslot_confirm_page_token'));
    headers.set("Accept", "application/json")
    const path = '/api/v1/coach/post_assign_time_slot_for_rescheduling_request'
    return this._http.post(path, timeslot_details, {headers}).pipe(
      map(r => r as any),
      catchError((err) => {
        this.checkUnauthorized(err)
        if (err.error && err.error.error && err.error.error.ERROR_CODE && err.error.error.ERROR_CODE == 'INVALID_TOKEN_ERROR') { // Cond for when request is made with a bad token.
        }
        trackFetchError('POST', path, err, timeslot_details)
        throw 'Error making an API request' + err; // Throw err.
      })
    )
  }

  getCoachDashboard(): Observable<CoachDashboardResponse> {
    return this.get<CoachDashboardResponse>("/api/v2/coach/dashboard")
  }

  submitPrepQuestions(sessionId: number, cmd: SubmitPrepQuestionsRequest): Observable<void> {
    return this.post<void>(`/api/v2/coach/sessions/${sessionId}/prep-questions/submit`, cmd)
  }

  getCoachJourneys(cmd: ListJourneysCommand): Observable<ListJourneysResponse> {
    let search = new URLSearchParams({
      offset: cmd.offset.toString(),
      limit: cmd.limit.toString(),
    });
    if (cmd.search) {
      search.set("search", cmd.search)
    }
    if (cmd.completed !== undefined) {
      search.set("completed", cmd.completed ? "true" : "false")
    }
    return this.get<ListJourneysResponse>("/api/v2/coach/journeys?" + search.toString())
  }

  fetchCoachJourneyFilesList(journey_id) {
    return this.post<any>('/api/v1/coach/post_fetch_coach_journey_files_list', journey_id)
  }

  coachUploadJourneyFiles(coach_journey_details) {
    return this.post<any>('/api/v1/coach/post_coach_upload_journey_files', coach_journey_details);
  }

  fetchCoachProfileDetails(coach_id) {
    return this.post<any>('/api/v1/coach/view_edit_coach', coach_id);
  }


  coachPasswordUpdate(password) {
    return this.post<any>('/api/v1/coach/post_update_coach_password', password);
  }

  createNextSessionPrepQuestions(journeyId: number, cmd: CreatePrepQuestionsRequest) {
    return this.post<PrepQuestionsResponse>(`/api/v2/coach/journeys/${journeyId}/next-session/prep-questions`, cmd)
  }

  markPrepQuestionAnswersSeen(sessionId: number) {
    return this.post<never>(`/api/v2/coach/sessions/${sessionId}/prep-questions/answers/seen`, null)
  }

  markPrepQuestionAnswersNotSeen(sessionId: number) {
    return this.del<never>(`/api/v2/coach/sessions/${sessionId}/prep-questions/answers/seen`)
  }

  bookNextSession(journeyId: number, cmd: BookNextSessionRequest){
    return this.post<any>(`/api/v2/coach/journeys/${journeyId}/next-session/book`, cmd)
  }

  endJourney(session_id) {
    return this.post<any>('/api/v1/coach/post_end_journey_and_add_key_takeaways', session_id)
  }

  coachJoinSession(journeyId, sessionId) {
    let endPoint = `/api/v1/coach/journeys/${journeyId}/sessions/${sessionId}/join`;
    return this.post<any>(endPoint, {})
  }

  fetchPrepQuestAndAnswers(session_id) {
    return this.post<any>('/api/v1/coach/post_fetch_session_preparation_question_and_answer', session_id);
  }

  fetchSessionEndedDetails(journey_id) {
    return this.post<any>('/api/v1/coach/post_fetch_sessions_ended_list', journey_id)
  }

  getCoachQualitricsQuestions(coach_id_and_language) {
    return this.post<any>('/api/v1/coach/post_fetch_coach_survey_questions', coach_id_and_language);
  }

  storeCoachQualitricsQuestionsAnswers(question_answers) {
    return this.post<any>('/api/v1/coach/post_save_coach_survey_questions_answers', question_answers)
  }

  reschedulingSession(rescheduling_info) {
    return this.post<any>('/api/v1/coach/post_coach_save_rescheduling_request', rescheduling_info)
  }

  getActionItemForCoachee(session_id) {
    return this.post<any>('/api/v1/coach/post_fetch_session_action_item_json', session_id)
  }

  getKeyTakeawaysForCoachee(journey_id) {
    return this.post<any>('/api/v1/coach/post_fetch_journey_key_takeway_json', journey_id);
  }

  saveActionItemsEditedInCoachingHistory(journey_details) {
    return this.post<any>('/api/v1/coach/post_add_actionitems', journey_details);
  }

  saveKeytakewaysEditedInCoachingHistory(journey_details) {
    return this.post<any>('/api/v1/coach/post_add_keytakeways', journey_details);
  }

  saveCoachSessionNotes(sessionId, notes) {
    return this.put<any>(`/api/v2/coach/sessions/${sessionId}/notes`, notes)
  }

  Delete_coach_file(file_id) {
    return this.post<any>('/api/v1/coach/delete_coach_file', file_id)
  }

  markDocumentAsSeen(journeyId, fileId) {
    let endpoint = `/api/v2/coach/journeys/${journeyId}/files/${fileId}/seen`;
    return this.post<any>(endpoint, '')
  }

  getAnalyticsConfiguration() {
    return this.get<any>('/api/v2/coach/analytics');
  }

  getProgramBriefingUrl(journeyId: number){
    return this.get(`/api/v2/coach/journeys/${journeyId}/program-briefing`);
  }

  getUserBriefingUrl(journeyId: number){
    return this.get(`/api/v2/coach/journeys/${journeyId}/coachee-briefing`);
  }

  getSessionById(sessionId: string|number, includePrepQuestions = false) {
    const include = includePrepQuestions ? "?include=prep-questions" : "";
    return this.get(`/api/v2/coach/sessions/${sessionId}${include}`);
  }

  getJourneyById (journeyId: number): Observable<JourneyDetailsResponse> {
    return this.get(`/api/v2/coach/journeys/${journeyId}`);
  }

  getFilePath(journeyId: number, fileId: number): string {
    return `/api/v2/coach/journeys/${journeyId}/files/${fileId}`
  }

}

function trackFetchError(method: string, path: string,  err: any, bodyData: any = null) {
  if (err?.status === 401) {
    //ignore unauthorized
    return
  }
  if (err?.error) {
    Sentry.captureException(err.error);
  }
}
